import type { PermissionMapping } from './permissions-types';

// Page route permissions
export const PAGE_PERMISSIONS: PermissionMapping[] = [
  // Home
  {
    path: '/',
    permission: null,
    name: 'View Home',
    description: 'Access to the home page',
  },

  // Search — open to all
  {
    path: '/search',
    permission: null,
    name: 'View Search',
    description: 'Access to search functionality',
  },

  // Region — admin only
  {
    path: '/region',
    permission: 'admin.view',
    name: 'View Region',
    description: 'Access to region overview',
  },
  {
    path: '/region/[...slug]',
    permission: 'admin.view',
    name: 'View Region Detail',
    description: 'Access to region details',
  },

  // NACE — admin only
  {
    path: '/nace',
    permission: 'admin.view',
    name: 'View NACE',
    description: 'Access to NACE overview',
  },
  {
    path: '/industry',
    permission: null,
    name: 'View Industry',
    description: 'Access to Industry overview',
  },
  {
    path: '/industry/[code]/[slug]',
    permission: null,
    name: 'View Industry Detail',
    description: 'Access to Industry specific metrics',
  },
  {
    path: '/nace/[code]',
    permission: 'admin.view',
    name: 'View NACE Detail',
    description: 'Access to NACE code details',
  },

  // Analysis
  {
    path: '/analysis',
    permission: 'analysis.view',
    name: 'View Analysis',
    description: 'Access to analysis page',
  },

  // Companies
  {
    path: '/companies/[euId]',
    permission: 'companies.view',
    name: 'View Company Detail',
    description: 'Access to company details',
  },

  // Admin — all gated on settings.manage
  {
    path: '/admin',
    permission: 'admin.view',
    name: 'View Admin',
    description: 'Access to admin dashboard',
  },
  {
    path: '/admin/users',
    permission: 'admin.view',
    name: 'View Users',
    description: 'Access to user analytics and management',
  },
  {
    path: '/admin/crm',
    permission: 'admin.view',
    name: 'View CRM',
    description: 'M&A sales pipeline',
  },
  {
    path: '/admin/subscriptions',
    permission: 'admin.view',
    name: 'Manage Subscriptions',
    description: 'Access to subscription management',
  },
  {
    path: '/admin/stripe-events',
    permission: 'admin.view',
    name: 'View Stripe Events',
    description: 'Access to Stripe events',
  },
  {
    path: '/admin/logs',
    permission: 'admin.view',
    name: 'View Logs',
    description: 'Access to system logs',
  },
  {
    path: '/admin/log-stats',
    permission: 'admin.view',
    name: 'View Log Statistics',
    description: 'Access to log statistics dashboard',
  },
  {
    path: '/admin/search-analytics',
    permission: 'admin.view',
    name: 'View Search Analytics',
    description: 'Filter usage and zero-result analysis for /search',
  },
  {
    path: '/admin/referrers',
    permission: 'admin.view',
    name: 'View Referrers',
    description: 'Where visitors arrive from, by source over time',
  },
  {
    path: '/vergabe',
    permission: null,
    name: 'View Tenders',
    description: 'Access to TED tender notices',
  },
  {
    path: '/admin/settings',
    permission: 'admin.view',
    name: 'Manage Settings',
    description: 'Access to system settings',
  },
  {
    path: '/admin/website',
    permission: 'admin.view',
    name: 'Manage Company Websites',
    description: 'Access to company website management',
  },
  {
    path: '/admin/overrides',
    permission: 'admin.view',
    name: 'Manage Company Overrides',
    description: 'Access to company data override management',
  },
  {
    path: '/admin/files',
    permission: 'admin.view',
    name: 'Manage Company Files',
    description: 'Access to company file upload management',
  },
  {
    path: '/admin/coverage',
    permission: 'admin.view',
    name: 'View Coverage',
    description: 'Access to data coverage dashboard',
  },
  {
    path: '/admin/privacy',
    permission: 'admin.view',
    name: 'Manage Private Companies',
    description: 'Access to private company management',
  },
  {
    path: '/admin/api',
    permission: 'admin.view',
    name: 'Manage API Keys (Admin)',
    description: 'Create API keys for customers and view filtered usage',
  },
  {
    path: '/admin/credits',
    permission: 'admin.view',
    name: 'Manage Credits (Admin)',
    description: 'Inspect and grant v1 API / MCP credits for a user',
  },
  {
    path: '/admin/emails',
    permission: 'admin.view',
    name: 'Email Templates (Admin)',
    description: 'Preview every transactional email and send a test copy',
  },
  {
    path: '/admin/publications',
    permission: 'admin.view',
    name: 'Publications Desk (Admin)',
    description: 'Topics, LinkedIn post drafts and chart cards from FirmenData data',
  },
  {
    path: '/admin/publications/topics/[id]',
    permission: 'admin.view',
    name: 'Publication Topic (Admin)',
    description: 'Fact pack, charts and post generation for one topic',
  },
  {
    path: '/admin/publications/posts/[id]',
    permission: 'admin.view',
    name: 'Publication Post (Admin)',
    description: 'Post preview, revisions, validation and publishing for one post',
  },
  {
    path: '/admin/funnel',
    permission: 'admin.view',
    name: 'Funnel (Admin)',
    description: 'Visitor to paid conversion by week',
  },
  {
    path: '/admin/watchlist',
    permission: 'admin.view',
    name: 'Watchlist Overview (Admin)',
    description: 'Who monitors which companies, and on what plan',
  },
  // Account — open to all authenticated users (auth guard handles login redirect)
  {
    path: '/account',
    permission: null,
    name: 'View Account',
    description: 'Access to account section',
  },
  {
    path: '/account/billing',
    permission: null,
    name: 'Manage Billing',
    description: 'Access to billing management',
  },
  {
    path: '/account/api-keys',
    permission: null,
    name: 'View API Keys',
    description: 'Access to API keys (handler enforces per-user scoping)',
  },
  {
    path: '/account/usage',
    permission: null,
    name: 'View API Usage',
    description:
      'Per-day API request and credit usage (handler enforces per-user scoping)',
  },

  // Plans — public pricing page, visible to unauthenticated users
  {
    path: '/plans',
    permission: null,
    name: 'View Plans',
    description: 'Access to plans page',
  },

  // Insolvenzticker — open to all
  {
    path: '/insolvenzticker',
    permission: null,
    name: 'View Insolvenzticker',
    description: 'Access to Insolvenzticker page',
  },

  // Neugründungen — open to all
  {
    path: '/neugruendungen',
    permission: null,
    name: 'View New Companies',
    description: 'Access to newly registered GmbHs',
  },

  // Product keywords — admin only
  {
    path: '/products',
    permission: 'admin.view',
    name: 'View Product Keywords',
    description: 'Access to product keywords overview',
  },
  {
    path: '/products/[[...params]]',
    permission: 'admin.view',
    name: 'View Product Keyword Detail',
    description: 'Access to product keyword detail pages',
  },

  // FAQ — open to all
  {
    path: '/faq',
    permission: null,
    name: 'View FAQ',
    description: 'Access to FAQ page',
  },
  {
    path: '/sources',
    permission: null,
    name: 'View Data Sources',
    description: 'Access to the data sources transparency page',
  },

  // Documentation — open to all
  {
    path: '/docs',
    permission: null,
    name: 'View Docs',
    description: 'Documentation index',
  },
  {
    path: '/docs/lists-import',
    permission: null,
    name: 'View List Import Docs',
    description: 'Documentation for the list import feature',
  },

  // APIs
  {
    path: '/developer/apis',
    permission: null,
    name: 'View APIs',
    description: 'Access to APIs overview page',
  },
  {
    path: '/developer/sdks',
    permission: null,
    name: 'View SDKs',
    description: 'Access to official SDKs overview page',
  },

  // Developer
  {
    path: '/developer/subscriptions',
    permission: null,
    name: 'View Subscriptions & Events',
    description:
      'Access to subscription event history (handler enforces per-user scoping)',
  },

  // Overview — open to all authenticated users.
  {
    path: '/overview',
    permission: null,
    name: 'View Overview',
    description: 'Access to the personal overview page',
  },
  // Activity — open to all authenticated users (the page enforces auth).
  {
    path: '/activity',
    permission: null,
    name: 'View Activity',
    description: 'Recent searches and company views (page enforces auth)',
  },
  // Watchlist — open to all authenticated users (the handler enforces auth).
  {
    path: '/watchlist',
    permission: null,
    name: 'View Watchlist',
    description: 'Access to the personal company watchlist page',
  },
  // My Lists (Pro) — page is open to all authenticated users so free users
  // see the upsell; creating/using lists is plan-gated in the API routes.
  {
    path: '/my-lists',
    permission: null,
    name: 'View My Lists',
    description: 'Overview of the personal company lists',
  },
  {
    path: '/my-lists/[listId]',
    permission: null,
    name: 'View List',
    description: 'One personal company list (handler enforces ownership)',
  },
  {
    path: '/account/notifications',
    permission: null,
    name: 'View Notifications',
    description: 'Full notification history (handler scopes to the caller)',
  },
  {
    path: '/account/downloads',
    permission: null,
    name: 'View Downloads',
    description:
      'Documents the user has downloaded (handler scopes to the caller)',
  },
];
